import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  PersonAdd,
  Delete,
  Email,
  Settings,
  Business,
  CloudUpload,
  CheckCircle,
  Warning,
  Refresh,
  Security
} from '@mui/icons-material';
import { useOrganization } from '../../hooks/useOrganization';
import api from '../../utilities/axios';
import { useTheme } from '../../styles/theme';
import useSnack from '../../hooks/useSnack';
import ConfirmDialog from '../../components/ConfirmDialog';

const ManageOrganization = () => {
  const navigate = useNavigate();
  const { selectedOrganization, selectOrganization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const { mode } = useTheme();
  const { SnackBar, showMessage } = useSnack();

  const sx = {
    divider: {
      marginTop: 2,
      marginBottom: 3.15,
    },
    sectionTitle: {
      fontWeight: 600,
      mb: 1,
    },
    cardPadding: {
      padding: 2,
      boxShadow: "none",
      border: (theme) => `1px solid ${theme.palette.custom.border}`,
      backgroundColor: mode === "dark" ? "#141414" : "#FFFFFF",
      borderRadius: 2,
      mb: 3,
    },
  };

  // Confirm Dialog State
  const [confirmConfig, setConfirmConfig] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmColor: 'primary'
  });

  const closeConfirm = () => setConfirmConfig(prev => ({ ...prev, open: false }));

  // Delete Dialog State (for the prompt replacement)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');



  // Organization Info State
  const [orgInfo, setOrgInfo] = useState({
    name: '',
    Email: '',
    Website: '',
    Address: '',
    Size: '',
    Country: '',
    ZipCode: '',
    City: '',
    CompanyIndustry: '',
    logo: null
  });

  // Members State
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', role: 'member' });

  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: '',
    user: '',
    pass: '',
    secure: true,
    useCustomSMTP: false,
    recoveryEmail: ''
  });

  const [savingSmtp, setSavingSmtp] = useState(false);

  const handleUpdateSMTP = async (updatedConfig = null) => {
    const configToSave = updatedConfig || smtpConfig;

    if (configToSave.useCustomSMTP && (!configToSave.host || !configToSave.port || !configToSave.user || !configToSave.pass)) {
      if (!updatedConfig) showMessage({ error: 'Please fill all SMTP fields' });
      return;
    }

    setSavingSmtp(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const response = await api.post(`/user/organizations/${selectedOrganization._id}/smtp`, {
        ...configToSave,
        recoveryEmail: configToSave.recoveryEmail || user.recoveryEmail || user.email,
        firstName: user.firstName
      });

      if (response.data.success) {
        showMessage({ success: 'SMTP settings updated successfully' });
        fetchData();
      }
    } catch (err) {
      showMessage({ error: err.response?.data?.message || 'Failed to update SMTP settings' });
    } finally {
      setSavingSmtp(false);
    }
  };

  const fetchData = useCallback(async () => {
    if (!selectedOrganization?._id) return;
    setLoading(true);
    try {
      const [orgRes, membersRes, invitesRes] = await Promise.all([
        api.get(`/user/organizations/${selectedOrganization._id}`),
        api.get(`/user/organizations/members`, { params: { organizationId: selectedOrganization._id } }),
        api.get(`/user/organizations/invitations`, { params: { organizationId: selectedOrganization._id } })
      ]);

      if (orgRes.data.success) {
        const org = orgRes.data.organization;
        
        // Role Check
        const user = JSON.parse(localStorage.getItem('user'));
        const userId = user?.id || user?._id;
        const member = org.members?.find(m => m.userId.toString() === userId.toString() || m.userId === userId);
        
        if (!member || !['owner', 'admin'].includes(member.role)) {
          showMessage({ error: 'You do not have permission to manage this organization' });
          setTimeout(() => {
            navigate('/');
          }, 2000);
          return;
        }

        setOrgInfo(org);
        if (org.smtpConfig) {
          setSmtpConfig({
            ...org.smtpConfig,
            recoveryEmail: org.Email // Default recovery email to org email
          });
        }
      }
      if (membersRes.data.success) setMembers(membersRes.data.members);
      if (invitesRes.data.success) setInvitations(invitesRes.data.invitations);
    } catch (err) {
      console.error('Error fetching organization data:', err);
      showMessage({ error: 'Failed to load organization details' });
      if (err.response?.status === 403) {
        showMessage({ error: 'You do not have permission to manage this organization' });
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedOrganization?._id, navigate, showMessage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const confirmDeleteOrg = async () => {
    if (deleteConfirmName !== orgInfo.name) {
      showMessage({ error: 'Organization name does not match. Please try again.' });
      return;
    }

    setLoading(true);
    try {
      const response = await api.delete(`/user/organizations/delete/${selectedOrganization._id}`);
      if (response.data.success) {
        showMessage({ success: 'Organization deleted successfully' });
        localStorage.removeItem('selectedOrganization');
        setTimeout(() => {
          navigate('/listOrganization');
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      showMessage({ error: err.response?.data?.message || 'Failed to delete organization' });
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmName('');
    setDeleteDialogOpen(true);
  };

  const handleInvite = async () => {
    setLoading(true);
    try {
      const response = await api.post(`/user/organizations/invite`, {
        ...inviteData,
        organizationId: selectedOrganization._id
      });
      if (response.data.success) {
        showMessage({ success: 'Invitation sent successfully' });
        setInviteDialogOpen(false);
        setInviteData({ email: '', role: 'member' });
        fetchData();
      }
    } catch (err) {
      showMessage({ error: err.response?.data?.message || 'Failed to send invitation' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = (memberId) => {
    setConfirmConfig({
      open: true,
      title: 'Remove Member',
      message: 'Are you sure you want to remove this member?',
      confirmLabel: 'Remove',
      confirmColor: 'error',
      onConfirm: async () => {
        closeConfirm();
        setLoading(true);
        try {
          const response = await api.delete(`/user/organizations/members/${memberId}`, { params: { organizationId: selectedOrganization._id } });
          if (response.data.success) {
            showMessage({ success: 'Member removed successfully' });
            fetchData();
          }
        } catch (err) {
          showMessage({ error: err.response?.data?.message || 'Failed to remove member' });
        } finally {
          setLoading(false);
        }
      }
    });
   };
 
  const handleUpdateMemberRole = async (memberId, newRole) => {
    setLoading(true);
    try {
      const response = await api.patch(`/user/organizations/members/${memberId}`, {
        organizationId: selectedOrganization._id,
        role: newRole
      });
      if (response.data.success) {
        showMessage({ success: 'Member role updated successfully' });
        fetchData();
      }
    } catch (err) {
      showMessage({ error: err.response?.data?.message || 'Failed to update role' });
    } finally {
      setLoading(false);
    }
  };

   const handleCancelInvitation = (invitationId) => {
    setConfirmConfig({
      open: true,
      title: 'Cancel Invitation',
      message: 'Are you sure you want to cancel this invitation?',
      confirmLabel: 'Cancel Invitation',
      confirmColor: 'error',
      onConfirm: async () => {
        closeConfirm();
        setLoading(true);
        try {
          const response = await api.delete(`/user/organizations/invitations/${invitationId}`, { params: { organizationId: selectedOrganization._id } });
          if (response.data.success) {
            showMessage({ success: 'Invitation cancelled successfully' });
            fetchData();
          }
        } catch (err) {
          showMessage({ error: err.response?.data?.message || 'Failed to cancel invitation' });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleResendInvitation = async (invitationId) => {
    setLoading(true);
    try {
      const response = await api.post(`/user/organizations/invitations/${invitationId}/resend`, { organizationId: selectedOrganization._id });
      if (response.data.success) {
        showMessage({ success: 'Invitation resent successfully' });
      }
    } catch (err) {
      showMessage({ error: err.response?.data?.message || 'Failed to resend invitation' });
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'owner': return <Alert icon={false} severity="error" sx={{ py: 0, px: 1, fontSize: '0.7rem' }}>Owner</Alert>;
      case 'admin': return <Alert icon={false} severity="warning" sx={{ py: 0, px: 1, fontSize: '0.7rem' }}>Admin</Alert>;
      default: return <Alert icon={false} severity="info" sx={{ py: 0, px: 1, fontSize: '0.7rem' }}>Member</Alert>;
    }
  };

  return (
    <Box p={0}>
      <Grid container spacing={1}>
        <Grid item md xs={12}>
          <Typography variant="h5" gutterBottom>
            Manage Organization
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Settings and members for {orgInfo.name}
          </Typography>
        </Grid>
        <Grid item md xs={12} align="right">
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/EditOrganization', { state: { org: orgInfo } })}
            sx={{ mr: 1 }}
          >
            Edit Organization
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAdd />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite Member
          </Button>
        </Grid>
      </Grid>
      <Divider light sx={sx.divider} />

      <Grid container spacing={3}>
        {/* General Settings Section */}
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle1" sx={sx.sectionTitle}>
            General Settings
          </Typography>
          <Box sx={sx.cardPadding}>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 1 }}>
                <Avatar
                  src={orgInfo.logo ? `${process.env.REACT_APP_SERVER || 'https://campaigns.clikkle.com:9000'}/static/${orgInfo.logo}` : ''}
                  sx={{ width: 80, height: 80, mb: 1, bgcolor: 'primary.main' }}
                >
                  {orgInfo.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="h6" className="capitalize">{orgInfo.name}</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Organization Email</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{orgInfo.Email}</Typography>
                
                <Typography variant="caption" color="textSecondary">Website</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{orgInfo.Website || 'N/A'}</Typography>
                
                <Typography variant="caption" color="textSecondary">Industry</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>{orgInfo.CompanyIndustry || 'N/A'}</Typography>
                
                <Typography variant="caption" color="textSecondary">Location</Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {orgInfo.Address ? `${orgInfo.Address}, ` : ''}
                  {orgInfo.City ? `${orgInfo.City}, ` : ''}
                  {orgInfo.Country || 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1.5 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>SMTP Settings</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={smtpConfig.useCustomSMTP}
                        onChange={(e) => {
                          const newConfig = { ...smtpConfig, useCustomSMTP: e.target.checked };
                          setSmtpConfig(newConfig);
                          handleUpdateSMTP(newConfig);
                        }}
                      />
                    }
                    label={<Typography variant="caption">Use custom SMTP</Typography>}
                    labelPlacement="start"
                  />
                </Box>
                
                {smtpConfig.useCustomSMTP && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="SMTP Host"
                        value={smtpConfig.host}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                        placeholder="smtp.example.com"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Port"
                        type="number"
                        value={smtpConfig.port}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, port: e.target.value })}
                        placeholder="587"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={smtpConfig.secure}
                            onChange={(e) => setSmtpConfig({ ...smtpConfig, secure: e.target.checked })}
                          />
                        }
                        label={<Typography variant="caption">SSL/TLS</Typography>}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="User"
                        value={smtpConfig.user}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, user: e.target.value })}
                        placeholder="user@example.com"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Password"
                        type="password"
                        value={smtpConfig.pass}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, pass: e.target.value })}
                        placeholder="********"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={() => handleUpdateSMTP()}
                        disabled={savingSmtp}
                        startIcon={savingSmtp && <CircularProgress size={16} />}
                      >
                        {savingSmtp ? 'Saving...' : 'Save & Verify SMTP'}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleDeleteClick}
                startIcon={<Delete />}
                disabled={loading}
                fullWidth
              >
                Delete Organization
              </Button>
            </Box>
          </Box>
        </Grid>

        {/* Members Section */}
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle1" sx={sx.sectionTitle}>
            Team Members
          </Typography>
          <Box sx={sx.cardPadding}>
            <List dense sx={{ p: 0 }}>
              {members.map((member, index) => (
                <React.Fragment key={member._id}>
                  <ListItem sx={{ py: 1.5, px: 0 }}>
                    <ListItemAvatar>
                      <Avatar src={member.userId?.picture} sx={{ width: 40, height: 40 }}>
                        {member.userId?.firstName?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {`${member.userId?.firstName} ${member.userId?.lastName || ''}`}
                          </Typography>
                          {member.role === 'owner' ? (
                            getRoleLabel(member.role)
                          ) : null}
                        </Box>
                      }
                      secondary={member.userId?.email}
                      primaryTypographyProps={{ fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.8rem' }}
                    />
                    <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {member.role !== 'owner' && (
                        <>
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={member.role}
                              onChange={(e) => handleUpdateMemberRole(member._id, e.target.value)}
                              sx={{ fontSize: '0.8rem', height: 32 }}
                            >
                              <MenuItem value="admin" sx={{ fontSize: '0.8rem' }}>Admin</MenuItem>
                              <MenuItem value="member" sx={{ fontSize: '0.8rem' }}>Member</MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton edge="end" color="error" size="small" onClick={() => handleRemoveMember(member._id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {member.role === 'owner' && (
                        <Box sx={{ width: 132 }} /> // Placeholder to keep layout consistent
                      )}
                    </Box>
                  </ListItem>
                  {index < members.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          </Box>

          {invitations.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ ...sx.sectionTitle, mt: 3 }}>
                Pending Invitations
              </Typography>
              <Box sx={sx.cardPadding}>
                <List dense sx={{ p: 0 }}>
                  {invitations.map((invite, index) => (
                    <React.Fragment key={invite._id}>
                      <ListItem sx={{ py: 1.5, px: 0 }}>
                        <ListItemAvatar>
                          <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.200', color: 'grey.600' }}>
                            <Email fontSize="small" />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={invite.email}
                          secondary={`Invited as ${invite.role}`}
                          primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
                          secondaryTypographyProps={{ fontSize: '0.8rem' }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" color="primary" size="small" onClick={() => handleResendInvitation(invite._id)} sx={{ mr: 1 }}>
                            <Refresh fontSize="small" />
                          </IconButton>
                          <IconButton edge="end" color="error" size="small" onClick={() => handleCancelInvitation(invite._id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < invitations.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            </>
          )}
        </Grid>
      </Grid>

      {/* Invite Member Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Invite New Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteData.email}
              onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteData.role}
                label="Role"
                onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleInvite}
            variant="contained"
            disabled={!inviteData.email || loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Organization Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Organization</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" gutterBottom>
              To delete this organization, please type its name: <strong>"{orgInfo.name}"</strong>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Type organization name"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmDeleteOrg}
            variant="contained"
            color="error"
            disabled={deleteConfirmName !== orgInfo.name || loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog 
        {...confirmConfig}
        onClose={closeConfirm}
      />
      {SnackBar}
    </Box>
  );
};

export default ManageOrganization;
